/*
 * Plan Builder — config defaults.
 *
 * Source-of-truth for every configurable copy string, scoring threshold,
 * and plan-engine narrative the public Plan Builder uses. The admin panel
 * lets operators override any of these; the public tool merges admin
 * overrides over these defaults at load time.
 *
 * Round A surface: brand, copy, scoring numbers, plan-engine narratives.
 * Out of scope (round B): ACTIONS, miracle keyword lexicons, match-
 * scoring weights. Those remain as module-level constants in
 * plan-generation.ts.
 */
import type {
  IhiDriver,
  PrimaryBlocker,
  PremortemReason,
  ResetLever,
} from './types';

// ─── Brand ──────────────────────────────────────────────────────
export interface BrandVars { [cssVarName: string]: string }

// ─── Welcome & Landing ──────────────────────────────────────────
export interface PlanBuilderContent {
  landing: {
    heroBadge: string;
    /**
     * Rendered via `dangerouslySetInnerHTML`. Only the bundled default is
     * fully trusted; if/when the admin editor surfaces this field, the
     * editor must restrict to a small whitelist of tags (`<br>`, `<em>`,
     * `<strong>`) and the public renderer should sanitize before
     * injection.
     */
    heroTitle: string;

    heroSub: string;
    ctaPrimary: string;
    meta: string;               // hero meta line ("Takes under 10 minutes…")
    howItWorks: ReadonlyArray<{ title: string; body: string }>;
  };
}

// ─── Lens phase ─────────────────────────────────────────────────
export interface LensStepCopy {
  heading: string;
  body: string;
  helper: string;
}
export interface LensCopy {
  step1: LensStepCopy;
  step2: LensStepCopy;
  step3: LensStepCopy;
  step4: LensStepCopy;
  step5: LensStepCopy;
  emotionalFeel?: ReadonlyArray<string>;
  emotionalAvoid?: ReadonlyArray<string>;
  flexibility?: ReadonlyArray<{ key: 'high' | 'medium' | 'low'; label: string; sub: string }>;
}

// ─── Diagnostic ─────────────────────────────────────────────────
export interface DiagnosticQuestionCopy {
  text: string;
  options?: ReadonlyArray<string>;
}
export interface DiagnosticCopy {
  op: { 1: DiagnosticQuestionCopy; 2: DiagnosticQuestionCopy; 3: DiagnosticQuestionCopy; 4: DiagnosticQuestionCopy; 5: DiagnosticQuestionCopy };
  exp: { 1: DiagnosticQuestionCopy; 2: DiagnosticQuestionCopy; 3: DiagnosticQuestionCopy; 4: DiagnosticQuestionCopy; 5: DiagnosticQuestionCopy; 6: DiagnosticQuestionCopy };
  miracle: { desk: string; midday: string; gc: string };
  gap1: DiagnosticQuestionCopy;
  gap2: { text: string; options: Record<PrimaryBlocker, string> };
  gap3: { text: string; options: Record<PremortemReason, string> };
}

// ─── Scoring ────────────────────────────────────────────────────
export interface ScoringCopy {
  stageThresholds: { s1Max: number; s2Max: number; s3Max: number };
  stages: {
    1: { name: string; tagline: string; desc: string };
    2: { name: string; tagline: string; desc: string };
    3: { name: string; tagline: string; desc: string };
    4: { name: string; tagline: string; desc: string };
  };
  ihiLabels: Record<IhiDriver, string>;
}

// ─── Plan engine narratives ─────────────────────────────────────
export interface PlanEngineCopy {
  resetLevers: Record<ResetLever, { part: 1 | 2; name: string; blurb: string }>;
  blockers: Record<PrimaryBlocker, { label: string; body: string }>;
  premortems: Record<PremortemReason, { risk: string; mit: string }>;
}

// ─── Results & Outreach copy ────────────────────────────────────
export interface ResultsCopy {
  disclosureLive: string;
  disclosureFetched: string;
  alignmentGapHeading: string;
  alignmentGapBody: string;
  part1Label: string;
  part1Title: string;
  part1Sub: string;
  part2Label: string;
  part2Title: string;
  /** Contains `{n}` placeholder substituted at render time with the action count. */
  part2Sub: string;
  brightsSpotBody: string;
  goalOfGoalHeading: string;
  goalOfGoalBody: string;
  miracleHeading: string;
  miracleSuffix: string;
  doLessHeading: string;
  doLessBody: string;
  joyProfileSummary: string;
  joyFootnote: string;
  preMortemTitle: string;
  preMortemSub: string;
  shareTitle: string;
  shareBody: string;
  footnoteLive: string;
}
export interface EmailCopy {
  panelTitle: string;
  panelBody: string;
  ctaLabel: string;
}
export interface TeamModalCopy {
  title: string;
  body: string;
  ctaLabel: string;
}

// ─── Aggregate ──────────────────────────────────────────────────
export interface PlanBuilderConfig {
  vars?: BrandVars;
  content: PlanBuilderContent;
  lens: LensCopy;
  diagnostic: DiagnosticCopy;
  scoring: ScoringCopy;
  planEngine: PlanEngineCopy;
  results: ResultsCopy;
  email: EmailCopy;
  teamModal: TeamModalCopy;
}

// ─── Default config ──────────────────────────────────────────────
export const DEFAULT_PLAN_BUILDER_CONFIG: PlanBuilderConfig = {
  content: {
    landing: {
      heroBadge: 'Evidence-Based Improvement Plan',
      heroTitle: "Make your legal team's next change<br />the one that <em>delivers</em>.",
      heroSub:
        "Legal teams are full of good ideas for improvement. What usually trips them up is bad timing, scope that's too big, or no safe place to start. This builds you a plan that's calibrated to where your team actually is, and designed for where you want to land.",
      ctaPrimary: 'Build my plan',
      meta: 'Takes under 10 minutes · Results save automatically',
      howItWorks: [
        {
          title: 'Your starting point',
          body: 'Where your team actually sits on the Reactive → Strategic spectrum today.',
        },
        {
          title: "What's draining your team",
          body: 'Six IHI drivers scored, so you know which pressure is costing you the most.',
        },
        {
          title: 'Two moves to make first',
          body: 'Research-grounded actions for the two drivers that will unlock the most progress.',
        },
        {
          title: 'How to avoid the failure mode',
          body: 'The most likely reason your next improvement effort fails — and how to sidestep it.',
        },
      ],
    },
  },
  lens: {
    step1: {
      heading: 'What progress is your team trying to make?',
      body: "Describe the job you're hiring an improvement plan to support — stated in a way that would be true whether you used software, paper processes, or nothing at all. This single sentence becomes the spine of everything below.",
      helper:
        'Try to describe the outcome, not the tool. Example: "Enable the business to execute agreements quickly while protecting the company from unacceptable risk."',
    },
    step2: {
      heading: 'Where does the work break down today?',
      body: 'Most diagnostic time gets wasted chasing symptoms. Picking the phase where the job actually breaks down focuses everything that follows.',
      helper:
        "If multiple are painful, pick the one that costs you the most time, money, or political capital.",
    },
    step3: {
      heading: "What three outcomes would tell you it's working?",
      body: 'These become the criteria everything is measured against. Phrase each as a measurable change — start with "Reduce" or "Increase" and name the thing you\'d watch.',
      helper:
        'Best when phrased as "Reduce X" or "Increase Y" with something measurable. Not enforced — just a hint.',
    },
    step4: {
      heading: 'When this is working, what should your team feel — and stop feeling?',
      body: "Functional fixes don't drive adoption — emotional ones do. The features can be perfect; if the system makes attorneys feel surveilled or makes your team look slow to the business, it gets quietly abandoned.",
      helper: 'Pick all that matter.',
    },
    step5: {
      heading: 'How much organizational change is coming?',
      body: 'Most legal departments underestimate how much their organization changes in 24 months. The plan we build will calibrate to this answer.',
      helper: 'How much organizational change do you expect in the next 24 months?',
    },
  },
  diagnostic: {
    op: {
      1: {
        text: 'Work arrives through a defined intake process rather than ad-hoc emails or messages.',
        options: ['1', '2', '3', '4', '5'],
      },
      2: {
        text: 'Leadership has real-time visibility into what the team is working on and its status.',
        options: ['1', '2', '3', '4', '5'],
      },
      3: {
        text: 'When capacity is under pressure, there is a clear process for prioritising and redistributing work.',
        options: ['1', '2', '3', '4', '5'],
      },
      4: {
        text: 'Legal Ops can configure and adapt team systems without depending on IT.',
        options: ['1', '2', '3', '4', '5'],
      },
      5: {
        text: 'Management views legal as a strategic partner, not just a cost centre or risk function.',
        options: ['1', '2', '3', '4', '5'],
      },
    },
    exp: {
      1: {
        text: 'Which best describes how your team experiences control over their work?',
        options: [
          'People rarely have say in how or when they do their work',
          'Some flexibility exists but major decisions are made above them',
          'People own their work and can shape how they approach it',
          'People actively drive improvements to their own practice',
        ],
      },
      2: {
        text: 'When something goes wrong or a concern arises, people on this team...',
        options: [
          'Stay quiet — surfacing problems feels risky',
          'Raise issues with trusted colleagues but rarely formally',
          'Feel comfortable raising concerns with their manager',
          "Proactively surface issues knowing they'll be heard without blame",
        ],
      },
      3: {
        text: 'How connected do people on this team feel to the impact of their work?',
        options: [
          'Work feels like a queue of tasks with no visible outcome',
          'Purpose is understood but rarely talked about',
          'People can connect their work to business outcomes',
          'People feel their work genuinely matters to the organisation',
        ],
      },
      4: {
        text: 'When decisions are made about how legal operates, the team...',
        options: [
          'Finds out after decisions are made',
          'Is occasionally consulted but rarely shapes the outcome',
          'Has structured ways to input into decisions',
          'Co-designs how the function operates',
        ],
      },
      5: {
        text: 'How often does the team have the space to improve how work gets done?',
        options: [
          "Never — it's all we can do to keep up with demand",
          'Rarely — improvement is theoretical, not practical',
          'Sometimes — we carve out space when possible',
          'Regularly — improvement is part of how we work',
        ],
      },
      6: {
        text: 'When the team does good work, it...',
        options: [
          'Goes unnoticed or is taken for granted',
          'Gets informal praise but rarely formal recognition',
          'Is acknowledged by leadership in a meaningful way',
          "Builds the team's reputation and influences how legal is funded",
        ],
      },
    },
    miracle: {
      desk: "The first thing you'd notice when you sit down at your desk",
      midday: 'What the team would be doing differently by midday',
      gc: 'The first thing your GC or business partner would notice, even if you said nothing',
    },
    gap1: {
      text: 'Management understands the operational reality of what it takes to run this legal team well.',
      options: ['1', '2', '3', '4', '5'],
    },
    gap2: {
      text: "If you had to name the single biggest thing getting in the way of a good day for this team, it would be...",
      options: {
        workload: 'Volume — there is simply too much work',
        process: 'Friction — the way work moves is broken',
        visibility: 'Invisibility — good work goes unseen',
        tools: 'Tools — the systems make work harder, not easier',
        culture: 'Culture — speaking up or changing things feels unsafe',
        clarity: "Clarity — people don't know what matters most",
      },
    },
    gap3: {
      text: 'If an improvement effort failed on this team, the most likely reason would be...',
      options: {
        leadership: "Leadership didn't stay committed after the initial push",
        capacity: 'The team had no capacity left to implement changes',
        buyin: 'Not everyone believed it would actually change anything',
        measurement: 'There was no way to know if it was working',
        scope: 'We tried to change too much at once',
        external: 'Something outside our control derailed it',
      },
    },
  },
  scoring: {
    stageThresholds: { s1Max: 1.8, s2Max: 2.6, s3Max: 3.4 },
    stages: {
      1: {
        name: 'Reactive',
        tagline: 'Work arrives informally',
        desc: 'No intake structure. Status-chasing is constant. Priorities are whoever shouts loudest. The team is running on goodwill and heroics.',
      },
      2: {
        name: 'Organised',
        tagline: 'Basic structure exists',
        desc: "Some intake and tracking. But it's fragmented — multiple tools, manual reporting, no single source of truth. The team is busy but not visible.",
      },
      3: {
        name: 'Operational',
        tagline: 'Demand is managed',
        desc: 'Demand is visible. Capacity is managed. Reporting is real-time. Legal Ops owns configuration, not IT. The team is trusted but not yet strategic.',
      },
      4: {
        name: 'Strategic',
        tagline: 'Legal is a trusted partner',
        desc: 'Legal is a high-trust, high-visibility function. AI is deployed with governance. Management funds expansion because ROI is proven.',
      },
    },
    ihiLabels: {
      autonomy: 'Autonomy & Choice',
      safety: 'Psychological Safety',
      meaning: 'Meaning & Purpose',
      participative: 'Participative Management',
      improvement: 'Daily Improvement',
      recognition: 'Recognition & Rewards',
    },
  },
  planEngine: {
    resetLevers: {
      go_and_see: {
        part: 1,
        name: 'Go and See the Work',
        blurb: 'Observe up close the reality of your work.',
      },
      goal_of_goal: {
        part: 1,
        name: 'Goal of the Goal',
        blurb: 'Identify alternate pathways to your ultimate destination.',
      },
      bright_spots: {
        part: 1,
        name: 'Study the Bright Spots',
        blurb: 'Analyse and replicate your own best work.',
      },
      target_constraint: {
        part: 1,
        name: 'Target the Constraint',
        blurb: 'Assess the #1 force that is holding you back.',
      },
      map_the_system: {
        part: 1,
        name: 'Map the System',
        blurb: 'Rise above the silos to spot promising targets for action.',
      },
      start_with_burst: {
        part: 2,
        name: 'Start with a Burst',
        blurb: 'Begin with an intense and focused period of work.',
      },
      recycle_waste: {
        part: 2,
        name: 'Recycle Waste',
        blurb: "Discontinue efforts that don't serve the mission.",
      },
      do_less_do_more: {
        part: 2,
        name: 'Do Less and Do More',
        blurb: 'Shift resources from lower-value work to higher-value.',
      },
      tap_motivation: {
        part: 2,
        name: 'Tap Motivation',
        blurb: "Prioritise the work that's required and desired.",
      },
      let_people_drive: {
        part: 2,
        name: 'Let People Drive',
        blurb: 'Give your team the autonomy to lead the change efforts.',
      },
      accelerate_learning: {
        part: 2,
        name: 'Accelerate Learning',
        blurb: 'Get better, faster feedback to guide your work.',
      },
    },
    blockers: {
      workload: {
        label: 'Volume',
        body: 'Too much work is the symptom. The cause is almost always upstream — no defined intake, no capacity model, or no permission to say "later". Work on the constraint, not the volume.',
      },
      process: {
        label: 'Friction',
        body: "Work moves through broken channels — rework, handoff failures, duplicated effort. Map the workflow end to end and you'll usually find one step where most of the time is lost.",
      },
      visibility: {
        label: 'Invisibility',
        body: 'Legal is doing the work but no one upstairs sees it. This quietly starves the function of resources and trust. Build the reporting that makes outcomes — not volume — legible to leadership.',
      },
      tools: {
        label: 'Tools',
        body: 'Systems are working against the team. Before buying a new tool, check whether existing tools are configured to match how work actually flows — most tool failures are configuration failures.',
      },
      culture: {
        label: 'Culture',
        body: 'Speaking up or changing things feels unsafe. This is the hardest constraint to move quickly, but the cheapest lever is how leaders respond when people do speak up. One "thanks for telling me" beats a dozen town-halls.',
      },
      clarity: {
        label: 'Clarity',
        body: "People don't know what matters most. Everything becomes equally urgent, which is the same as nothing being urgent. Name the top three priorities for the quarter and hold the rest to \"if time\".",
      },
    },
    premortems: {
      leadership: {
        risk: 'Leadership commitment fades after the initial push',
        mit: "Anchor the change to a metric on leadership's dashboard from day one. What gets measured gets sustained. If the GC can't answer \"how is this going?\" in their monthly review, the initiative is invisible — and invisible initiatives die.",
      },
      capacity: {
        risk: 'The team has no capacity left to implement changes',
        mit: "Heath's Reset framework is explicit: you must restack resources before you can improve. Identify one low-value activity the team can stop doing to create 10% capacity before any improvement effort begins.",
      },
      buyin: {
        risk: "Scepticism — people don't believe this time will be different",
        mit: 'Start with a burst sprint on the highest-irritation pebble the team names. A visible win in 30 days builds more credibility than any strategy document. Let people see the system actually change before asking them to believe in it.',
      },
      measurement: {
        risk: "No way to know if it's working",
        mit: "Pfeffer & Sutton's evidence-based management principle: measure what matters, not what's easy. Run a 5-question pulse survey before and after your first sprint. Publish the results to the team — transparency about progress is itself a driver of motivation.",
      },
      scope: {
        risk: 'Trying to change too much at once',
        mit: "One leverage point. One burst sprint. One metric. Heath's core insight: a small, focused intervention at the right point in the system produces disproportionate results. Resist the urge to fix everything — it's how nothing gets fixed.",
      },
      external: {
        risk: "Something outside the team's control derails progress",
        mit: 'Build the change at the team level, not the organisational level. IHI research shows that frontline teams can improve joy in work even when the broader system is resistant. Contain the scope of your sprint to what the team controls.',
      },
    },
  },
  results: {
    disclosureLive:
      'Your plan displays only to you in this browser. Anonymised responses are captured so we can improve the tool and publish aggregate research on what legal teams are facing — nothing identifying you is included unless you choose to share it below.',
    disclosureFetched:
      "You're viewing a saved plan. Anyone with this link can revisit it.",
    alignmentGapHeading: 'Management alignment gap detected.',
    alignmentGapBody:
      "Your responses suggest a significant gap between how leadership perceives legal's operational stage and where the team actually is. This is one of the most common drivers of under-investment and burnout in legal teams.",
    part1Label: 'Part 1 · Reset',
    part1Title: 'Your leverage points',
    part1Sub:
      'Where a little bit of effort is likely to yield a disproportionate return. Before thinking about action, get clear on where to push.',
    part2Label: 'Part 2 · Reset',
    part2Title: 'Your resource stack',
    part2Sub:
      'How to reallocate time, effort, and attention onto those leverage points. These are the {n} actions most likely to move your specific diagnostic result — ordered by fit.',
    brightsSpotBody:
      "Use this as the anchor for your improvement effort. Ask: what already works here, and can any of it be transplanted to the places that aren't working?",
    goalOfGoalHeading: 'Before committing to the plan below — ask this.',
    goalOfGoalBody:
      'What is the team ultimately trying to deliver? If you achieved perfect operational maturity and nothing else changed, would the business actually be better off? Sometimes the honest answer reveals that "stronger legal ops" is the wrong target — and a smaller, different thing is the real goal.',
    miracleHeading: 'You said the morning after the miracle would look like:',
    miracleSuffix:
      'Hold this. The plan below is the smallest set of moves we think most likely to get you closer to that morning. If anything in the plan feels disconnected from what you wrote, trust your answer over our recommendation.',
    doLessHeading: 'To make room for the actions above, name one thing to stop.',
    doLessBody:
      'Every improvement starts with a deletion. Look at the last month of team work — what consumed time without moving any goal? Commit to reducing or removing one thing before starting the first action. New work without stopped work is just a bigger pile.',
    joyProfileSummary:
      "How we read the team's state — Joy in Work profile",
    joyFootnote:
      '↓ indicates priority areas. Scoring based on IHI Framework for Improving Joy in Work (Perlo et al., 2017).',
    preMortemTitle: 'Pre-Mortem: your most likely failure mode',
    preMortemSub:
      "Based on Gary Klein's prospective hindsight technique (HBR, 2007)",
    shareTitle: 'Take this plan with you',
    shareBody:
      "A plan your leadership doesn't see is a plan that won't get funded. Print it, copy it, or bring this page to your next team meeting.",
    footnoteLive:
      'Your plan is saved automatically. Return at any time to update your responses as the team evolves.',
  },
  email: {
    panelTitle: 'Send this plan by email',
    panelBody: "Send the plan to up to 5 addresses at a time. We'll only email the addresses you enter.",
    ctaLabel: 'Email this plan',
  },
  teamModal: {
    title: 'Run this with your whole team',
    body: "Each team member completes the plan individually. You see an aggregated view — where the team agrees, where it doesn't, and where the pressure is concentrated. We're rolling this out to selected teams first. Leave your details and we'll be in touch.",
    ctaLabel: 'Request early access',
  },
};
